import { useCallback, useEffect, useMemo, useState } from 'react'
import { Report } from 'notiflix'
import api from '../../../helpers/apiRequester'
import { Button, Card, EmptyState, NativeSelect, SearchInput, SegmentedControl, Spinner, StatCard, StatGrid, StatusPill, Tabs } from '../../../ui'
import { CategoryMeta, ReplyDetailPane } from '../commsInboxView/ReplyDetailPane'
import { ReplyListItem, humanise } from '../commsInboxView/shared'
import { ConversationRow, EmailRow } from './ConversationRow'
import { ConversationTrail } from './ConversationTrail'
import { ExportGroupBy, ExportRequest, downloadCommsExport } from '../commsShared/exportDownload'
import { CategoryActions, CategoryStats, ReportMenu, SeeActionsLink, SenderLine, SenderSplit, StatLine, sumSenders } from './CategoryReport'
import { agentActionsHref } from '../commsShared/agentActions'
import { Bucket, COUNTERPARTY_LABEL, ConversationCounts, ConversationListItem, ConversationState, CounterpartyKind, LETTER_KIND_ORDER, MailboxFunnel, STATE_LABEL, bucketHint, bucketLabel, letterBucket, letterKindOf } from './types'
import styles from './styles.module.scss'

type SubTab = 'all' | Bucket
type GroupBy = 'none' | 'category' | 'state'
/**
 * threads: one row per thread, cards count threads by where they stand (outcome).
 * emails:  one row per email received, cards count emails by their own category.
 */
type View = 'threads' | 'emails'

/** Category filter value for "not yet categorised" (the API reads it as latest_category IS NULL). */
const NO_CATEGORY = '__none'
/** Threads made only of automated mail (bounces, auto replies, sales): nothing to action. */
const AUTOMATED_ONLY = '__automated'
const VIEW_STORAGE_KEY = 'comms.receivedView'

/** Filter key a thread falls under in the Threads view: its outcome, or one of the two "no outcome" groups. */
const outcomeKeyOf = (r: ConversationListItem) => r.outcomeCategory ?? (r.inboundCount > 0 && r.automatedCount >= r.inboundCount ? AUTOMATED_ONLY : NO_CATEGORY)

export type ReceivedView = View

/** The view the Received tab last used in this browser; threads unless a person chose emails. */
export const readReceivedView = (): View => {
    try {
        return window.localStorage.getItem(VIEW_STORAGE_KEY) === 'emails' ? 'emails' : 'threads'
    } catch {
        return 'threads'
    }
}

/** Remember which view the Received tab should open in (the Overview uses this before opening a slice). */
export const rememberReceivedView = (view: View) => {
    try {
        window.localStorage.setItem(VIEW_STORAGE_KEY, view)
    } catch {
        /* private mode */
    }
}

/** Category cards shown on the Received tab before the rest collapse into a table ("All categories" is extra). */
const STRIP_MAX_CARDS = 6
/** With this many or fewer, all are cards. */
const STRIP_ALL_CARDS_UP_TO = 8

/** One category summary card under a tab: emails in that category at that level. */
interface BucketCategory {
    key: string | null
    label: string
    description: string | null
    nextAction: string | null
    /** Emails (emails view) or threads (threads view). */
    total: number
    /** Of those, with a client or claim. */
    linked: number
    last7d: number
    /** Threads view only: emails inside these threads, and how many are automated. */
    emails: number | null
    automated: number | null
    /** Who they came from: a client, a lender, or neither. */
    from?: SenderSplit
    /** Threads view: threads at this outcome still to sub-categorise (null = this outcome has never been deep-dived). */
    deepDivePending?: number | null
}

/** What POST comms/inbox/export needs for one card under one tab. Waiting emails have no category yet, so nothing to export. */
const exportSliceFor = (bucket: SubTab, category: string | null, view: View): Omit<ExportRequest, 'mailbox'> | null => {
    if (bucket === 'waiting') return null
    const kind = letterKindOf(bucket)
    if (view === 'threads') {
        // One row per thread, keyed by where it stands; the API reads __none / __automated itself.
        return { kind: 'thread_outcome', category, sendKind: kind, includeArchived: bucket === 'all', archivedOnly: bucket === 'archived' }
    }
    const cat = category === null ? null : category === NO_CATEGORY ? 'unclassified' : category
    if (kind) return { kind: 'letter_type_category', sendKind: kind, category: cat }
    // The All tab counts archived emails too; the Archived tab is only them; Read excludes them.
    return { kind: 'category', category: cat, includeArchived: bucket === 'all', archivedOnly: bucket === 'archived' }
}

/**
 * Tabs: the mailbox's pipeline states plus one container per type of letter we
 * have sent that has replies here (letters of complaint first). Letter types
 * with nothing in them stay hidden until they are used.
 */
const tabsFor = (direction: 'received' | 'sent', counts: ConversationCounts | null, emails: MailboxFunnel | null, current: SubTab): SubTab[] => {
    const letterCount = (k: string) => (direction === 'received' && emails ? emails.byLetter.find((l) => l.kind === k)?.total ?? 0 : counts?.byLetter?.[k] ?? 0)
    const letters = LETTER_KIND_ORDER.filter((k) => k === 'initial_loc' || letterCount(k) > 0 || current === letterBucket(k)).map(letterBucket)
    return direction === 'sent' ? ['all', ...letters, 'archived'] : ['all', 'waiting', ...letters, 'archived']
}

/** Body of a category card: the figures line by line, then "Report ▾" (every row, or grouped by lender / claim / client). */
const CategorySub = ({ lines, from = null, unit, canExport, exporting, onExport, actionsHref }: { lines: StatLine[]; from?: SenderSplit | null; unit: 'email' | 'thread'; canExport: boolean; exporting: boolean; onExport: (groupBy: ExportGroupBy | null) => void; actionsHref: string }) => (
    <span className={styles.categorySub}>
        <CategoryStats lines={lines} from={from} />
        <CategoryActions>
            {canExport && <ReportMenu unit={unit} exporting={exporting} onExport={onExport} />}
            <SeeActionsLink href={actionsHref} />
        </CategoryActions>
    </span>
)

interface Props {
    /** null = every mailbox. */
    mailbox: string | null
    direction: 'received' | 'sent'
    counts: ConversationCounts | null
    /** Email totals for this scope; Received tab badges count emails so they agree with the category cards. */
    emails: MailboxFunnel | null
    categoryMeta: Map<string, CategoryMeta>
    categoryOptions: { value: string; label: string }[]
    /** URL-backed state so a conversation can be shared. */
    selectedId: string | null
    onSelect: (id: string | null) => void
    /** Threads or emails: what every figure on the Received side counts. Owned by the workspace so the Received badge agrees. */
    view?: View
    onViewChange?: (view: View) => void
    initialBucket?: SubTab
    initialCategory?: string
    initialClaim?: string
    initialState?: '' | ConversationState
    onFiltersChanged?: (f: { bucket: SubTab; category: string; claim: string; state: '' | ConversationState }) => void
    onChanged?: () => void
}

/**
 * Conversations for one mailbox in a master/detail layout: buckets across the
 * top, filters and grouping on the left, the full trail on the right.
 */
export const ConversationList = ({ mailbox, direction, counts, emails, categoryMeta, categoryOptions, selectedId, onSelect, view: viewProp = 'threads', onViewChange, initialBucket = 'all', initialCategory = '', initialClaim = '', initialState = '', onFiltersChanged, onChanged }: Props) => {
    const [bucket, setBucket] = useState<SubTab>(initialBucket === 'read' ? 'all' : initialBucket)
    const tabs = useMemo(() => tabsFor(direction, counts, emails, bucket), [direction, counts, emails, bucket])
    const [category, setCategory] = useState(initialCategory)
    // Sent is always threads (a letter and what came back).
    const view: View = direction === 'received' ? viewProp : 'threads'
    const setView = (next: View) => {
        if (next === view) return
        setCategory('')
        setSelectedEmail(null)
        setEmailThreadOpen(false)
        onViewChange?.(next)
    }
    const selectEmail = (row: ReplyListItem | null) => {
        setSelectedEmail(row)
        setEmailThreadOpen(false)
        // An email is open, not a thread: keep the shareable URL honest.
        if (selectedId) onSelect(null)
    }
    const [claim, setClaim] = useState(initialClaim)
    const [kind, setKind] = useState<'' | CounterpartyKind>('')
    const [state, setState] = useState<'' | ConversationState>(initialState)
    const [groupBy, setGroupBy] = useState<GroupBy>('none')
    const [searchTerm, setSearchTerm] = useState('')
    const [appliedSearch, setAppliedSearch] = useState('')
    const [rows, setRows] = useState<ConversationListItem[]>([])
    const [emailRows, setEmailRows] = useState<ReplyListItem[]>([])
    // Emails view: the one email open on the right, and whether its whole thread has been asked for.
    const [selectedEmail, setSelectedEmail] = useState<ReplyListItem | null>(null)
    const [emailThreadOpen, setEmailThreadOpen] = useState(false)
    const [total, setTotal] = useState(0)
    const [loading, setLoading] = useState(true)
    const [version, setVersion] = useState(0)
    const [bucketCategories, setBucketCategories] = useState<{ bucket: SubTab; view: View; total: number; linked: number; items: BucketCategory[] } | null>(null)
    const [exporting, setExporting] = useState<string | null>(null)
    const [showAllCategories, setShowAllCategories] = useState(false)
    // The biggest categories stay as cards; the long tail collapses into a table. The active
    // filter's category is always a card so it is never hidden while it is applied.
    const { stripCards, stripRest } = useMemo(() => {
        const items = bucketCategories?.items ?? []
        if (items.length <= STRIP_ALL_CARDS_UP_TO) return { stripCards: items, stripRest: [] as BucketCategory[] }
        const cards = items.slice(0, STRIP_MAX_CARDS)
        const rest = items.slice(STRIP_MAX_CARDS)
        const activeIdx = rest.findIndex((c) => (c.key ?? NO_CATEGORY) === category)
        if (activeIdx >= 0) cards.push(...rest.splice(activeIdx, 1))
        return { stripCards: cards, stripRest: rest }
    }, [bucketCategories, category])
    const exportCategory = async (category: string | null, groupBy: ExportGroupBy | null = null) => {
        const slice = exportSliceFor(bucket, category, view)
        if (!slice) return
        const key = category ?? '__all'
        setExporting(key)
        try {
            await downloadCommsExport({ ...slice, groupBy, mailbox, includeUnlinked: true, rowPerEmail: true }, `comms-${mailbox ?? 'all-mailboxes'}-${view}-${bucket}-${(category ?? 'all').replace(/^__/, '')}${groupBy ? `-by-${groupBy}` : ''}.xlsx`)
        } finally {
            setExporting(null)
        }
    }

    // Summary cards for the current tab at this level (all / waiting / a letter
    // type / archived): threads by outcome, or emails by category. Received side only.
    useEffect(() => {
        if (direction !== 'received') return
        let cancelled = false
        api({ url: 'comms/inbox/bucket-categories', data: { sourceMailbox: mailbox, bucket, view } })
            .then((resp) => {
                if (cancelled || !resp.data?.success) return
                setBucketCategories({ bucket, view, total: resp.data.total || 0, linked: resp.data.linked || 0, items: resp.data.categories || [] })
            })
            .catch(() => {
                if (!cancelled) setBucketCategories(null)
            })
        return () => {
            cancelled = true
        }
    }, [mailbox, bucket, direction, view, version])

    useEffect(() => {
        onFiltersChanged?.({ bucket, category, claim, state })
    }, [bucket, category, claim, state, onFiltersChanged])

    const load = useCallback(async () => {
        setLoading(true)
        try {
            if (view === 'emails') {
                // One row per email received; the category filter is the email's own category.
                const resp = await api({
                    url: 'comms/inbox/list',
                    data: {
                        sourceMailbox: mailbox,
                        bucket,
                        classification: category || null,
                        partyType: kind === 'lender' || kind === 'client' ? kind : null,
                        claimRef: claim || null,
                        search: appliedSearch || null,
                        limit: 200,
                    },
                })
                if (resp.data?.success) {
                    setEmailRows(resp.data.replies || [])
                    setTotal(resp.data.total || 0)
                }
                return
            }
            // One row per thread; the category filter is where the thread stands (its outcome).
            const resp = await api({
                url: 'comms/inbox/conversations',
                data: {
                    sourceMailbox: mailbox,
                    bucket: bucket === 'all' ? null : bucket,
                    outcome: category || null,
                    counterpartyKind: kind || null,
                    state: state || null,
                    claimRef: claim || null,
                    direction: direction === 'sent' ? 'sent' : null,
                    // Received: only threads with an email received, so the list agrees with the cards.
                    hasInbound: direction === 'received',
                    search: appliedSearch || null,
                    limit: 200,
                },
            })
            if (resp.data?.success) {
                setRows(resp.data.conversations || [])
                setTotal(resp.data.total || 0)
            }
        } catch (err: any) {
            Report.failure('Load failed', err?.response?.data?.message || `Could not load ${view}.`, 'Okay')
        } finally {
            setLoading(false)
        }
    }, [mailbox, bucket, category, kind, state, claim, direction, appliedSearch, view])

    useEffect(() => {
        load()
    }, [load, version])

    const refresh = () => {
        setVersion((v) => v + 1)
        onChanged?.()
    }

    const categoryLabel = (key: string | null) => (key === AUTOMATED_ONLY ? 'Automated mail only' : key && key !== NO_CATEGORY ? categoryMeta.get(key)?.label ?? humanise(key) : 'Not yet categorised')
    const grouped = useMemo(() => {
        if (groupBy === 'none' || view === 'emails') return null
        const groups = new Map<string, ConversationListItem[]>()
        for (const r of rows) {
            const key = groupBy === 'category' ? outcomeKeyOf(r) : r.state
            if (!groups.has(key)) groups.set(key, [])
            groups.get(key)!.push(r)
        }
        return Array.from(groups.entries()).sort(([, a], [, b]) => b.length - a.length)
    }, [rows, groupBy, view])

    const groupLabel = (key: string) => (groupBy === 'category' ? categoryLabel(key) : STATE_LABEL[key as ConversationState] ?? humanise(key))

    const countFor = (t: SubTab) => {
        if (direction === 'received' && view === 'emails' && emails) {
            if (t === 'all') return emails.held
            if (t === 'waiting') return emails.waiting
            if (t === 'read') return emails.read
            if (t === 'archived') return emails.archived
            const kind = letterKindOf(t)
            return kind ? emails.byLetter.find((l) => l.kind === kind)?.total ?? 0 : undefined
        }
        if (!counts) return undefined
        // Received threads: only threads with an email received, so "All" equals the cards.
        if (t === 'all') return direction === 'received' ? counts.withInbound ?? counts.conversations : counts.conversations
        if (t === 'waiting') return counts.waiting
        if (t === 'read') return counts.read
        if (t === 'archived') return counts.archived
        const kind = letterKindOf(t)
        return kind ? counts.byLetter?.[kind] ?? 0 : undefined
    }
    const tabItems = tabs.map((t) => ({
        value: t,
        label: t === 'all' ? 'All' : bucketLabel(t),
        count: countFor(t),
    }))

    const hasFilters = Boolean(category || kind || state || claim || appliedSearch)
    const unit = view === 'threads' ? 'thread' : 'email'
    const units = (n: number) => `${unit}${n === 1 ? '' : 's'}`

    const renderRow = (r: ConversationListItem) => <ConversationRow key={r.id} row={r} selected={r.id === selectedId} onSelect={onSelect} categoryLabel={categoryLabel} showMailbox={mailbox === null} />
    const renderEmailRow = (r: ReplyListItem) => <EmailRow key={r.id} row={r} selected={r.id === selectedEmail?.id} onSelect={selectEmail} categoryLabel={categoryLabel} showMailbox={mailbox === null} />

    const stripLines = (c: BucketCategory): StatLine[] => {
        const lines: StatLine[] = [{ value: c.linked, label: 'with a client or claim' }]
        if (view === 'threads' && c.emails !== null && c.emails !== c.total) lines.push({ value: c.emails, label: 'emails inside' })
        if (view === 'threads' && c.automated && c.key !== AUTOMATED_ONLY) lines.push({ value: c.automated, label: 'automated' })
        lines.push({ value: c.last7d, label: 'this week' })
        return lines
    }

    return (
        <div className={styles.stack}>
            {direction === 'received' && (
                <div className={styles.viewRow}>
                    <SegmentedControl<View>
                        className={styles.viewToggle}
                        aria-label="Count by"
                        value={view}
                        onChange={setView}
                        options={[
                            { value: 'threads', label: 'Threads', count: counts?.withInbound ?? undefined },
                            { value: 'emails', label: 'Emails', count: emails?.held ?? undefined },
                        ]}
                    />
                </div>
            )}
            <div>
                <Tabs<SubTab> value={bucket} onChange={setBucket} items={tabItems} noMargin />
                <p className={styles.tabHint}>{bucket === 'all' ? (direction === 'sent' ? 'Every conversation that contains a letter we sent or a reply from us.' : mailbox ? `Every ${unit} in this mailbox, newest first.` : `Every ${unit} across all mailboxes, newest first.`) : bucketHint(bucket)}</p>
            </div>

            {direction === 'received' && bucketCategories && bucketCategories.bucket === bucket && bucketCategories.view === view && bucketCategories.items.length > 0 && (
                <section className={styles.categoryStrip} aria-label={view === 'threads' ? 'Threads by outcome' : 'Emails by category'}>
                    <StatGrid minColumn={230}>
                        <StatCard
                            wrapLabel
                            className={styles.categoryStatCard}
                            label={view === 'threads' ? 'All threads' : 'All categories'}
                            value={bucketCategories.total}
                            sub={<CategorySub lines={[{ value: bucketCategories.linked, label: 'with a client or claim' }]} from={sumSenders(bucketCategories.items.map((c) => c.from))} unit={unit} canExport={exportSliceFor(bucket, null, view) !== null} exporting={exporting === '__all'} onExport={(g) => exportCategory(null, g)} actionsHref={agentActionsHref({ mailbox, view, bucket, category: null })} />}
                            active={!category}
                            onClick={() => setCategory('')}
                        />
                        {stripCards.map((c) => {
                            const value = c.key ?? NO_CATEGORY
                            return (
                                <StatCard
                                    key={value}
                                    wrapLabel
                                    className={styles.categoryStatCard}
                                    label={
                                        c.deepDivePending != null && c.deepDivePending > 0 ? (
                                            <>
                                                {c.label}{' '}
                                                <StatusPill size="sm" tone="warn" title="Threads at this outcome whose newest email has not been sub-categorised yet (Deep dive on the Agent Actions page)">
                                                    {c.deepDivePending.toLocaleString('en-GB')} to sub-categorise
                                                </StatusPill>
                                            </>
                                        ) : (
                                            c.label
                                        )
                                    }
                                    value={c.total}
                                    sub={<CategorySub lines={stripLines(c)} from={c.from ?? null} unit={unit} canExport={exportSliceFor(bucket, value, view) !== null} exporting={exporting === value} onExport={(g) => exportCategory(value, g)} actionsHref={agentActionsHref({ mailbox, view, bucket, category: value })} />}
                                    active={category === value}
                                    onClick={() => setCategory(category === value ? '' : value)}
                                />
                            )
                        })}
                    </StatGrid>
                    {stripRest.length > 0 && (
                        <div className={styles.moreCategories}>
                            <Button size="sm" variant="link" onClick={() => setShowAllCategories((v) => !v)} aria-expanded={showAllCategories}>
                                {showAllCategories ? 'Hide' : 'Show'} {stripRest.length.toLocaleString('en-GB')} smaller {view === 'threads' ? 'outcome' : 'categor'}{stripRest.length === 1 ? (view === 'threads' ? '' : 'y') : view === 'threads' ? 's' : 'ies'} · {stripRest.reduce((n, c) => n + c.total, 0).toLocaleString('en-GB')} {units(stripRest.reduce((n, c) => n + c.total, 0))}
                            </Button>
                            {showAllCategories && (
                                <div className={styles.tableWrap}>
                                    <table className={styles.table}>
                                        <thead>
                                            <tr>
                                                <th>{view === 'threads' ? 'Outcome' : 'Category'}</th>
                                                <th className={styles.num}>{view === 'threads' ? 'Threads' : 'Emails'}</th>
                                                <th className={styles.num}>With a client or claim</th>
                                                <th>From</th>
                                                <th className={styles.num}>This week</th>
                                                <th>Next step</th>
                                                <th />
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {stripRest.map((c) => {
                                                const value = c.key ?? NO_CATEGORY
                                                return (
                                                    <tr key={value} className={styles.clickableRow} onClick={() => setCategory(category === value ? '' : value)} title={c.description ?? undefined}>
                                                        <td>
                                                            <span className={styles.categoryLabel}>{c.label}</span>
                                                        </td>
                                                        <td className={styles.num}>{c.total.toLocaleString('en-GB')}</td>
                                                        <td className={styles.num}>{c.linked.toLocaleString('en-GB')}</td>
                                                        <td className={styles.muted}>{c.from ? <SenderLine from={c.from} /> : '—'}</td>
                                                        <td className={styles.num}>{c.last7d > 0 ? c.last7d.toLocaleString('en-GB') : '—'}</td>
                                                        <td className={styles.muted}>{c.nextAction ?? '—'}</td>
                                                        <td className={styles.rowActions} onClick={(e) => e.stopPropagation()}>
                                                            <Button size="sm" variant="link" onClick={() => setCategory(category === value ? '' : value)}>
                                                                {category === value ? 'Clear filter' : 'Filter'}
                                                            </Button>
                                                            {exportSliceFor(bucket, value, view) !== null && (
                                                                <ReportMenu unit={unit} exporting={exporting === value} onExport={(g) => exportCategory(value, g)} />
                                                            )}
                                                            <SeeActionsLink href={agentActionsHref({ mailbox, view, bucket, category: value })} />
                                                        </td>
                                                    </tr>
                                                )
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}
                </section>
            )}

            <div className={styles.layout}>
                <aside className={styles.master}>
                    <Card padding="sm">
                        <form
                            className={styles.stack}
                            style={{ gap: 8 }}
                            onSubmit={(e) => {
                                e.preventDefault()
                                setAppliedSearch(searchTerm.trim())
                            }}
                        >
                            <SearchInput
                                value={searchTerm}
                                onChange={(v) => {
                                    setSearchTerm(v)
                                    if (!v) setAppliedSearch('')
                                }}
                                placeholder="Subject, sender, claim ref or summary…"
                            />
                            <div className={styles.filterGrid}>
                                <NativeSelect
                                    small
                                    aria-label={view === 'threads' ? 'Outcome' : 'Category'}
                                    value={category}
                                    onChange={(e) => setCategory(e.target.value)}
                                    options={[{ value: '', label: view === 'threads' ? 'All outcomes' : 'All categories' }, ...categoryOptions, { value: NO_CATEGORY, label: 'Not yet categorised' }, ...(view === 'threads' ? [{ value: AUTOMATED_ONLY, label: 'Automated mail only' }] : [])]}
                                />
                                <NativeSelect small aria-label="Who" value={kind} onChange={(e) => setKind(e.target.value as '' | CounterpartyKind)} options={[{ value: '', label: 'Lenders and clients' }, { value: 'lender', label: 'Lenders' }, { value: 'client', label: 'Clients' }, ...(view === 'threads' ? [{ value: 'other', label: 'Other' }] : [])]} />
                                {view === 'threads' && <NativeSelect small aria-label="State" value={state} onChange={(e) => setState(e.target.value as '' | ConversationState)} options={[{ value: '', label: 'Any state' }, { value: 'awaiting_us', label: 'Awaiting us' }, { value: 'awaiting_lender', label: 'Awaiting lender' }, { value: 'awaiting_client', label: 'Awaiting client' }, { value: 'closed', label: 'Closed' }]} />}
                                {view === 'threads' && <NativeSelect small aria-label="Group by" value={groupBy} onChange={(e) => setGroupBy(e.target.value as GroupBy)} options={[{ value: 'none', label: 'No grouping' }, { value: 'category', label: 'Group by outcome' }, { value: 'state', label: 'Group by state' }]} />}
                            </div>
                            {hasFilters && (
                                <div className={styles.activeFilters}>
                                    <span>Showing:</span>
                                    {claim && (
                                        <StatusPill size="sm" tone="accent">
                                            claim {claim}
                                        </StatusPill>
                                    )}
                                    {category && (
                                        <StatusPill size="sm" tone="info">
                                            {categoryLabel(category)}
                                        </StatusPill>
                                    )}
                                    {kind && <StatusPill size="sm">{COUNTERPARTY_LABEL[kind]}</StatusPill>}
                                    {state && <StatusPill size="sm">{STATE_LABEL[state]}</StatusPill>}
                                    {appliedSearch && <StatusPill size="sm">“{appliedSearch}”</StatusPill>}
                                    <Button
                                        type="button"
                                        size="sm"
                                        variant="link"
                                        onClick={() => {
                                            setCategory('')
                                            setKind('')
                                            setState('')
                                            setClaim('')
                                            setSearchTerm('')
                                            setAppliedSearch('')
                                        }}
                                    >
                                        Clear
                                    </Button>
                                </div>
                            )}
                        </form>
                    </Card>

                    <div className={styles.listHeader}>
                        <h3 className={styles.listTitle}>
                            {view === 'emails' ? 'Emails' : 'Threads'}
                            <StatusPill round size="sm">
                                {total}
                            </StatusPill>
                        </h3>
                        <Button size="sm" variant="ghost" onClick={refresh} disabled={loading}>
                            Refresh
                        </Button>
                    </div>

                    {loading ? (
                        <Spinner block text={`Loading ${units(2)}…`} />
                    ) : view === 'emails' ? (
                        emailRows.length === 0 ? (
                            <EmptyState bare title="Nothing here" description="No emails match the current tab and filters." />
                        ) : (
                            <ul className={styles.convList}>{emailRows.map(renderEmailRow)}</ul>
                        )
                    ) : rows.length === 0 ? (
                        <EmptyState bare title="Nothing here" description="No threads match the current tab and filters." />
                    ) : grouped ? (
                        <ul className={styles.convList}>
                            {grouped.map(([key, items]) => (
                                <li key={key} className={styles.convGroup}>
                                    <div className={styles.convGroupHeader}>
                                        <span>{groupLabel(key)}</span>
                                        <StatusPill round size="sm">
                                            {items.length}
                                        </StatusPill>
                                    </div>
                                    <ul className={styles.convList}>{items.map(renderRow)}</ul>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <ul className={styles.convList}>{rows.map(renderRow)}</ul>
                    )}
                </aside>

                <section className={styles.detail}>
                    {view === 'emails' ? (
                        // One email on its own. The thread is a click away, never shown uninvited.
                        emailThreadOpen && selectedEmail?.conversationId ? (
                            <>
                                <div className={styles.emailPaneBar}>
                                    <Button size="sm" variant="link" onClick={() => setEmailThreadOpen(false)}>
                                        ← Back to the email
                                    </Button>
                                    <span className={styles.muted}>The whole thread this email belongs to, with the email open in place.</span>
                                </div>
                                <ConversationTrail
                                    conversationId={selectedEmail.conversationId}
                                    initialMessageId={selectedEmail.id}
                                    categoryMeta={categoryMeta}
                                    onChanged={refresh}
                                    onShowAllOnClaim={(ref) => {
                                        setClaim(ref)
                                        setBucket('all')
                                    }}
                                />
                            </>
                        ) : (
                            <>
                                {selectedEmail && (
                                    <div className={styles.emailPaneBar}>
                                        <span className={styles.muted}>One email. </span>
                                        {selectedEmail.conversationId ? (
                                            <Button size="sm" variant="link" onClick={() => setEmailThreadOpen(true)} title="See the letter we sent, every reply and our follow-ups around this email">
                                                Show the whole thread →
                                            </Button>
                                        ) : (
                                            <span className={styles.muted}>Not part of a thread yet.</span>
                                        )}
                                    </div>
                                )}
                                <ReplyDetailPane
                                    replyId={selectedEmail?.id ?? null}
                                    categoryMeta={categoryMeta}
                                    onChanged={refresh}
                                    onShowAllEmails={(ref) => {
                                        setClaim(ref)
                                        setBucket('all')
                                    }}
                                    emptyTitle="Select an email"
                                    emptyDescription="Pick an email to read it on its own: what it says, its category, and the client and claim it relates to."
                                />
                            </>
                        )
                    ) : (
                        <ConversationTrail
                            conversationId={selectedId}
                            categoryMeta={categoryMeta}
                            onChanged={refresh}
                            onShowAllOnClaim={(ref) => {
                                setClaim(ref)
                                setBucket('all')
                            }}
                        />
                    )}
                </section>
            </div>
        </div>
    )
}
